/*

How to use:
 Add the class "table-sort" to the table element.

 To make a column not sortable, add the attribute "data-table-sort-disabled"
 to the thead > tr > td element.

 To sort a column by a numeric value (rather than a string), add the
 attribute "data-table-sort-numeric" to the thead > tr > td element.

 To use a different value when sorting (not the text of the given cell), add
 the attribute 'data-table-sort-value="your-value"' to the tbody > tr > td element.

*/

interface SortData {
    currentSort: {
        cell: HTMLElement | null;
        order: 'asc' | 'desc' | null;
        numeric: boolean | null;
    },
    indicator: HTMLSpanElement | null;
    cache: Map<HTMLElement, string|number>;
    cells: Map<HTMLElement, HTMLElement[]>;
}

/* Selectors that match both a real <table> and a "fake" table built from
 * non-table elements carrying the role classes (.table/.thead/.tbody/.tr/.td). */
const HEADER_CELL_SELECTOR = 'thead tr td, .thead .tr .td';
const BODY_ROW_SELECTOR = 'tbody tr, .tbody .tr';

/* A real <tr> exposes its cells via .cells; a fake row (e.g. an element with
 * the .tr class) does not, so fall back to its direct .td children. */
function cellsOf(row: HTMLElement): HTMLElement[] {
    if (row instanceof HTMLTableRowElement) {
        return Array.from(row.cells);
    }

    return Array.from(row.querySelectorAll<HTMLElement>(':scope > .td'));
}

class TableSortEvent extends Event {
    private readonly _fragment: DocumentFragment;
    private readonly _data: SortData;

    constructor(fragment: DocumentFragment, data: SortData, init?: EventInit) {
        super('tableSort', init);
        this._fragment = fragment;
        this._data = data;
    }

    public get fragment() {
        return this._fragment;
    }

    public get data() {
        return this._data;
    }
}

export function initTableSortSingle(table: HTMLElement) {
    const map = new Map<HTMLElement, SortData>;

    map.set(table, { currentSort: { cell: null, order: null, numeric: null }, indicator: null, cache: new Map(), cells: new Map() });
    const headerCells = table.querySelectorAll<HTMLElement>(HEADER_CELL_SELECTOR);
    const rows = Array.from(table.querySelectorAll<HTMLElement>(BODY_ROW_SELECTOR));

    rows.forEach((row, index) => {
        row.dataset.tableSortIndex = index.toString(10);
        const cells = cellsOf(row);
        map.get(table).cells.set(row, cells);

        for (let i = 0; i < cells.length; i++) {
            const cell = cells[i];
            const headerCell = headerCells[i];
            let value: string | number = cell.dataset.tableSortValue === undefined
                ? cell.innerText
                : cell.dataset.tableSortValue;

            if (headerCell.dataset.tableSortNumeric !== undefined) {
                const res = parseFloat(value);
                value = isNaN(res) ? Number.MAX_VALUE : res;
            } else {
                value = value.toLowerCase();
            }

            map.get(table).cache.set(cell, value);
        }
    });

    headerCells.forEach((cell, index) => {
        if (cell.dataset.tableSortDisabled !== undefined) {
            return;
        }

        cell.classList.add('table-sort-header');
        cell.addEventListener('click', () => {
            const data = map.get(table);

            if (data.indicator === null) {
                data.indicator = document.createElement('span');
                data.indicator.classList.add('table-sort-indicator');
                data.indicator.innerText = '>';
            }

            if (data.currentSort.cell === cell) {
                if (data.currentSort.order === null) {
                    data.currentSort.order = 'asc';
                    cell.classList.add('table-sort-asc');
                    cell.appendChild(data.indicator);
                } else if (data.currentSort.order === 'asc') {
                    data.currentSort.order = 'desc';
                    cell.classList.remove('table-sort-asc');
                    cell.classList.add('table-sort-desc');
                } else {
                    data.currentSort.order = null;
                    cell.classList.remove('table-sort-desc');
                    cell.removeChild(data.indicator);
                }
            } else {
                if (data.currentSort.cell && data.currentSort.order !== null) {
                    data.currentSort.cell.classList.remove('table-sort-asc');
                    data.currentSort.cell.classList.remove('table-sort-desc');
                    data.currentSort.cell.removeChild(data.indicator);
                }

                data.currentSort.cell = cell;
                data.currentSort.order = 'asc';
                cell.appendChild(data.indicator);
                cell.classList.add('table-sort-asc');
            }

            data.currentSort.numeric = data.currentSort.cell.dataset.tableSortNumeric !== undefined;

            sortTable(rows, data, index, table);
        });
    });
}

export function initTableSort() {
    const tables = document.querySelectorAll<HTMLElement>('.table-sort');

    tables.forEach(table => {
        initTableSortSingle(table);
    });
}

function sortTable(rows: HTMLElement[], data: SortData, n: number, table: HTMLElement) {
    const tbody = rows[0].parentElement;
    const collator = new Intl.Collator('da', { numeric: true });

    rows.sort((a, b) => {
        const cellA = data.cells.get(a)[n];
        const cellB = data.cells.get(b)[n];

        if (!data.currentSort.order) {
            return parseInt(cellA.parentElement.dataset.tableSortIndex) - parseInt(cellB.parentElement.dataset.tableSortIndex);
        }

        if (data.currentSort.numeric && data.currentSort.order == 'asc') {
            return <number>data.cache.get(cellA) - <number>data.cache.get(cellB);
        } else if (data.currentSort.numeric && data.currentSort.order == 'desc') {
            return <number>data.cache.get(cellB) - <number>data.cache.get(cellA);
        } else if (data.currentSort.order == 'asc') {
            return collator.compare(<string>data.cache.get(cellA), <string>data.cache.get(cellB));
        } else if (data.currentSort.order == 'desc') {
            return collator.compare(<string>data.cache.get(cellB), <string>data.cache.get(cellA));
        }
    });

    const fragment = document.createDocumentFragment();
    rows.forEach(row => fragment.appendChild(row));

    table.dispatchEvent(new TableSortEvent(fragment, data, { bubbles: true }));

    tbody.appendChild(fragment);
}
